// src/redux/features/blogsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    collection,
    addDoc,
    deleteDoc,
    updateDoc,
    doc,
    getDocs,
    getDoc,
    query,
    orderBy,
    serverTimestamp,
    where,
    limit,
    startAfter
} from 'firebase/firestore';
import { sendEmails } from './emailSlice'; // Import the thunk
import { db } from '../../firebase/config';

// Store page caches to avoid refetching data
const pageCache = new Map();

// Async thunks for blogs
export const fetchBlogs = createAsyncThunk(
    'blogs/fetchBlogs',
    async ({ page = 1, pageSize = 6 }, { getState }) => {
        const blogsRef = collection(db, 'blogs');
        
        // Check if we have this page cached already
        const cacheKey = `page_${page}_size_${pageSize}`;
        if (pageCache.has(cacheKey)) {
            return pageCache.get(cacheKey);
        }
        
        let q;
        
        // For first page, just get first batch
        if (page === 1) {
            q = query(
                blogsRef,
                orderBy('createdAt', 'desc'),
                limit(pageSize)
            );
        } else {
            // For other pages, we'll need to fetch all prior data first to get the cursor
            // This is a limitation of Firestore pagination
            
            // Get previous page's data to find the last document
            const prevPageKey = `page_${page-1}_size_${pageSize}`;
            let lastVisibleDoc;
            
            if (pageCache.has(prevPageKey)) {
                // If we have the previous page cached, use its last doc
                const prevPageData = pageCache.get(prevPageKey);
                const lastDocId = prevPageData.blogs[prevPageData.blogs.length - 1].id;
                const lastDocRef = doc(db, 'blogs', lastDocId);
                const lastDocSnapshot = await getDoc(lastDocRef);
                lastVisibleDoc = lastDocSnapshot;
            } else {
                // Otherwise we need to fetch enough documents to get to our offset
                const prevDocsQuery = query(
                    blogsRef,
                    orderBy('createdAt', 'desc'),
                    limit((page - 1) * pageSize)
                );
                const prevDocsSnapshot = await getDocs(prevDocsQuery);
                lastVisibleDoc = prevDocsSnapshot.docs[prevDocsSnapshot.docs.length - 1];
            }
            
            // Now query with the cursor
            q = query(
                blogsRef,
                orderBy('createdAt', 'desc'),
                startAfter(lastVisibleDoc),
                limit(pageSize)
            );
        }
        
        const querySnapshot = await getDocs(q);
        
        // Get total count for pagination
        const countSnapshot = await getDocs(query(blogsRef));
        const totalDocs = countSnapshot.size;
        const totalPages = Math.ceil(totalDocs / pageSize);
        
        const blogs = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt ? data.createdAt.toDate().toISOString() : new Date().toISOString(),
                updatedAt: data.updatedAt ? data.updatedAt.toDate().toISOString() : new Date().toISOString()
            };
        });

        // Create result object
        const result = {
            blogs,
            pagination: {
                currentPage: page,
                totalPages,
                totalDocs,
                pageSize
            }
        };
        
        // Cache this page's results
        pageCache.set(cacheKey, result);
        
        return result;
    }
);

export const fetchBlogById = createAsyncThunk(
    'blogs/fetchBlogById',
    async (blogId) => {
        const blogRef = doc(db, 'blogs', blogId);
        const blogDoc = await getDoc(blogRef);
        if (!blogDoc.exists()) {
            throw new Error('Blog not found');
        }
        const data = blogDoc.data();
        return {
            id: blogDoc.id,
            ...data,
            createdAt: data.createdAt.toDate().toISOString(),
            updatedAt: data.updatedAt.toDate().toISOString()
        };
    }
);

// New function to fetch all blogs for admin purposes
export const fetchAllBlogs = createAsyncThunk(
    'blogs/fetchAllBlogs',
    async () => {
        const blogsRef = collection(db, 'blogs');
        const q = query(blogsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const blogs = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt || { seconds: Date.now() / 1000, nanoseconds: 0 }
            };
        });

        return blogs;
    }
);

// Clear cache when creating, updating, or deleting blogs
const clearCache = () => {
    pageCache.clear();
};

export const createBlog = createAsyncThunk(
    'blogs/createBlog',
    async (blogData, { dispatch }) => {
        // Clear cache since data has changed
        clearCache();
        
        const blogsRef = collection(db, 'blogs');
        const docRef = await addDoc(blogsRef, {
            ...blogData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        const newBlog = {
            id: docRef.id,
            ...blogData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        // Only send emails if the blog is published
        if (blogData.published) {
            // Fetch active subscribers
            const subscribersRef = collection(db, 'subscribers');
            const q = query(subscribersRef, where('isActive', '==', true));
            const querySnapshot = await getDocs(q);
            const subscribers = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            if (blogData.published && subscribers.length > 0) {
                dispatch(sendEmails({ subscribers, blogData, emailType: 'new' }));
                console.log(`Emails dispatched for updated blog "${blogData.title}" to ${subscribers.length} subscribers.`);
            }
        }
        return newBlog;
    }
);

export const updateBlog = createAsyncThunk(
    'blogs/updateBlog',
    async ({ blogId, updates }, { dispatch }) => {
        // Clear cache since data has changed
        clearCache();
        
        const blogRef = doc(db, 'blogs', blogId);
        const updateData = {
            ...updates,
            updatedAt: serverTimestamp()
        };
        await updateDoc(blogRef, updateData);
        // send email if blog is published
        if (updates.published) {
            const blogDoc = await getDoc(blogRef);
            const blogData = {
                id: blogDoc.id,
                ...blogDoc.data()
            };
            // Fetch active subscribers
            const subscribersRef = collection(db, 'subscribers');
            const q = query(subscribersRef, where('isActive', '==', true));
            const querySnapshot = await getDocs(q);
            const subscribers = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            if (updates.published && subscribers.length > 0) {

                dispatch(sendEmails({ subscribers, blogData, emailType: 'updated' }));
                console.log(`Emails dispatched for updated blog "${blogData.title}" to ${subscribers.length} subscribers.`);

            }
        }

        return { id: blogId, updates };
    }
);

export const deleteBlog = createAsyncThunk(
    'blogs/deleteBlog',
    async (blogId) => {
        // Clear cache since data has changed
        clearCache();
        
        await deleteDoc(doc(db, 'blogs', blogId));
        return blogId;
    }
);

const blogsSlice = createSlice({
    name: 'blogs',
    initialState: {
        items: [],
        currentBlog: null,
        status: 'idle',
        error: null,
        pagination: {
            currentPage: 1,
            totalPages: 0,
            pageSize: 6,
            totalDocs: 0,
            lastVisible: null
        }
    },
    reducers: {
        clearCurrentBlog: (state) => {
            state.currentBlog = null;
        },
        setCurrentPage: (state, action) => {
            state.pagination.currentPage = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch all blogs
            .addCase(fetchBlogs.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchBlogs.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload.blogs;
                state.pagination = {
                    ...state.pagination,
                    ...action.payload.pagination
                };
            })
            .addCase(fetchBlogs.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Fetch single blog
            .addCase(fetchBlogById.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchBlogById.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.currentBlog = action.payload;
            })
            .addCase(fetchBlogById.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Fetch all blogs (for admin)
            .addCase(fetchAllBlogs.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchAllBlogs.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload;
            })
            .addCase(fetchAllBlogs.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Create blog
            .addCase(createBlog.fulfilled, (state, action) => {
                state.items.unshift(action.payload);
            })
            // Update blog
            .addCase(updateBlog.fulfilled, (state, action) => {
                const { id, updates } = action.payload;
                const blog = state.items.find(blog => blog.id === id);
                if (blog) {
                    Object.assign(blog, updates);
                }
                if (state.currentBlog?.id === id) {
                    Object.assign(state.currentBlog, updates);
                }
            })
            // Delete blog
            .addCase(deleteBlog.fulfilled, (state, action) => {
                state.items = state.items.filter(blog => blog.id !== action.payload);
                if (state.currentBlog?.id === action.payload) {
                    state.currentBlog = null;
                }
            });
    }
});

export const { clearCurrentBlog, setCurrentPage } = blogsSlice.actions;
export default blogsSlice.reducer;